import { useState } from 'react';
import { ChevronLeft, ChevronRight, ClipboardCheck } from 'lucide-react';
import type { Compliance } from '@techsofit/api-types';
import { Card, Button, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import { getCompliance } from '../lib/gymApi';

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function mondayOf(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7));
}
const DAY_FMT = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short' });

function barColor(pct: number): string {
  if (pct >= 80) return 'bg-ok-ink';
  if (pct >= 50) return 'bg-warn';
  return 'bg-bad-ink';
}

/** Coach view: who's hitting their assigned workouts this week. */
export function CompliancePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const from = isoDate(weekStart);
  const to = isoDate(addDays(weekStart, 6));
  const { data, loading } = useAsync(() => getCompliance(from, to), [from, to]);
  const rows = data ?? [];
  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.percent, 0) / rows.length) : 0;

  return (
    <section>
      <PageHeader
        title="Compliance"
        description="Who's hitting their assigned workouts. Lowest first — give them a nudge."
      />

      <div className="mb-4 flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))}>
          This week
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
          <ChevronRight className="size-4" aria-hidden />
        </Button>
        <span className="ml-1 text-sm text-muted">
          {DAY_FMT.format(weekStart)} – {DAY_FMT.format(addDays(weekStart, 6))}
        </span>
        {rows.length > 0 && (
          <span className="ml-auto rounded-pill bg-surface px-3 py-1 text-sm font-semibold text-ink">{avg}% avg</span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-card" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck className="size-6" aria-hidden />}
          title="No assigned workouts this week"
          description="Schedule workouts to members or groups, and their compliance shows up here."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <ComplianceRow key={r.memberId} row={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function ComplianceRow({ row }: { row: Compliance }) {
  return (
    <Card className="flex items-center gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate font-medium text-ink">{row.memberName}</p>
          <p className="shrink-0 text-sm text-muted">
            {row.completed}/{row.assigned} done
          </p>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-pill bg-surface">
          <div className={cn('h-full rounded-pill transition-all', barColor(row.percent))} style={{ width: `${row.percent}%` }} />
        </div>
      </div>
      <span className="w-10 shrink-0 text-right font-display text-lg font-semibold text-ink">{row.percent}%</span>
    </Card>
  );
}
